import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Banner,
  Card,
  DefinitionList,
  DefinitionRow,
  EmptyState,
  IdChip,
  RewardPill,
  StatusPill,
  Table,
  TCell,
  THead,
  TRow,
} from '@cou/admin-ui';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import {
  defaultPlayersClient,
  type AdminPlayerDetail,
  type PlayersClient,
} from '../players/players-client';
import { JurisdictionBadge, SegmentPill } from '../players/badges';
import { entryPillState } from '../entries/entry-state';
import { awardPillState } from '../awards/award-state';
import { tierLabel } from '../awards/tier-label';
import { toRewardType } from '../contests/reward-cell';
import { formatDateParts } from '../contests/format-date';

/**
 * One player (API-21, admin-ui.md → Players › Detail): the profile, their Entries across Contests
 * with a drill-down to the Picks, and their Awards with delivery state. The support flow's pivot —
 * account ref → player → entries → picks → awards, all under view permissions.
 *
 * View-only by construction: entry and pick voids live in Settlement behind `contests:settle`
 * (API-11), and the award Retry lives on the Awards section behind `awards:retry`. Nothing here
 * mutates anything.
 */

export interface PlayerDetailScreenProps {
  /** Injected for tests; defaults to the real API-21 client. */
  readonly client?: PlayersClient;
}

export function PlayerDetailScreen({ client = defaultPlayersClient }: PlayerDetailScreenProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getToken } = useAdminAuth();
  const [player, setPlayer] = useState<AdminPlayerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = getToken();
    if (token === null || id === undefined) return;
    const result = await client.getPlayer(token, id);
    if (result.ok) {
      setPlayer(result.player);
      setError(null);
    } else {
      setError(result.message ?? 'The player lookup failed.');
      setPlayer(null);
    }
  }, [client, getToken, id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null) {
    return (
      <AdminShell title="Player profile">
        <Banner data-testid="player-error" variant="danger">
          {error}
        </Banner>
      </AdminShell>
    );
  }

  if (player === null) return <AdminShell title="Player profile" />;

  const segment = player.segmentLabel ?? player.segment;

  return (
    <AdminShell
      title="Player profile"
      subtitle="Pseudonymous account reference — no name or email exists for this player."
      meta={
        <>
          <IdChip label="ACCT" value={player.account} />
          <SegmentPill>{segment}</SegmentPill>
          <JurisdictionBadge>{player.state}</JurisdictionBadge>
        </>
      }
    >
      {/* Two columns (7fr / 13fr in the design): the profile card, then the activity cards. */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[7fr_13fr]">
        <Card data-testid="player-profile" eyebrow="Profile" title="Account">
          <DefinitionList>
            <DefinitionRow label="Account ref" value={player.account} muted />
            <DefinitionRow label="Segment" value={<SegmentPill>{segment}</SegmentPill>} />
            <DefinitionRow
              label="Jurisdiction"
              value={<JurisdictionBadge>{player.state}</JurisdictionBadge>}
            />
            <DefinitionRow
              label="First seen"
              value={formatDateParts(player.createdAt).date}
              muted
            />
            <DefinitionRow
              label="Last seen"
              value={formatDateParts(player.lastSeenAt).date}
              muted
            />
            <DefinitionRow label="Lifetime entries" value={player.entryCount} />
            <DefinitionRow label="Lifetime awards" value={player.awards.length} />
            {/* Terms acceptance is a profile field (decision 7), so it stays even though the design
                foregrounds the lifetime counts. */}
            <DefinitionRow
              label="Terms accepted"
              value={
                player.termsAcceptedAt === null ? '—' : formatDateParts(player.termsAcceptedAt).date
              }
              muted
            />
          </DefinitionList>
        </Card>

        <div className="flex flex-col gap-4">
          <Card title="Entries">
            {player.entries.length === 0 ? (
              <div data-testid="player-entries-empty">
                <EmptyState
                  variant="section"
                  title="No entries"
                  body="This player has not entered a contest yet."
                />
              </div>
            ) : (
              <Table columns="minmax(0,1.5fr) 120px 90px 150px 32px">
                <THead>
                  <TCell>Contest</TCell>
                  <TCell>State</TCell>
                  <TCell>Correct</TCell>
                  <TCell>Submitted</TCell>
                  <TCell />
                </THead>
                {player.entries.map((entry) => (
                  <TRow
                    key={entry.id}
                    data-testid={`player-entry-${entry.id}`}
                    onClick={() => void navigate(`/entries/${entry.id}`)}
                    className="cursor-pointer"
                  >
                    <TCell variant="primary">{entry.contestName}</TCell>
                    <TCell>
                      <StatusPill kind="entry" state={entryPillState(entry.status)} />
                    </TCell>
                    <TCell variant="mono" data-testid="entry-correct">
                      {correctLabel(entry.status, entry.correctPicks)}
                    </TCell>
                    <TCell variant="mono">
                      {entry.submittedAt === null ? '—' : formatDateParts(entry.submittedAt).date}
                    </TCell>
                    <TCell className="text-ink-4">›</TCell>
                  </TRow>
                ))}
              </Table>
            )}
          </Card>

          <Card title="Awards">
            {player.awards.length === 0 ? (
              <div data-testid="player-awards-empty">
                <EmptyState
                  variant="section"
                  title="No awards"
                  body="Awards appear once a winning entry settles."
                />
              </div>
            ) : (
              <Table columns="minmax(0,1.4fr) 100px minmax(0,1fr) 120px 130px">
                <THead>
                  <TCell>Contest</TCell>
                  <TCell>Tier</TCell>
                  <TCell>Reward</TCell>
                  <TCell>State</TCell>
                  <TCell>Delivered</TCell>
                </THead>
                {player.awards.map((award) => (
                  <TRow key={award.id} data-testid={`player-award-${award.id}`}>
                    <TCell>
                      <IdChip value={award.contestId.slice(0, 8)} title={award.contestId} />
                    </TCell>
                    <TCell>{tierLabel(award.tier)}</TCell>
                    <TCell>
                      <RewardPill
                        type={toRewardType(award.rewardType) ?? 'freebet'}
                        label={award.displayValue}
                      />
                    </TCell>
                    <TCell>
                      <StatusPill kind="award" state={awardPillState(award.status)} />
                    </TCell>
                    <TCell variant="mono">
                      {award.deliveredAt === null ? '—' : formatDateParts(award.deliveredAt).date}
                    </TCell>
                  </TRow>
                ))}
              </Table>
            )}
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}

/**
 * The correct-count as the design shows it: "N/6" for a settled Entry (v1 is always exactly 6
 * picks), an em-dash before settlement — a Locked Entry has no meaningful count yet.
 */
function correctLabel(status: string, correctPicks: number): string {
  return status === 'Settled' ? `${correctPicks}/6` : '—';
}
